<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { DocCategoryValues } from '@/types/docs'
import type { DocCategory, DocCategorySummary, DocArticleListItem } from '@/types/docs'

const props = defineProps<{
  categories: DocCategorySummary[]
  currentCategory: DocCategory
  currentSlug: string
  articlesInCurrentCategory: DocArticleListItem[]
  base: string
}>()

const emit = defineEmits<{
  navigate: []
}>()

const { t } = useI18n()

// Build a map for quick lookup
const countByCategory = computed(() => {
  const map: Record<string, number> = {}
  for (const c of props.categories) {
    map[c.slug] = c.article_count
  }
  return map
})

function categoryTitle(slug: DocCategory): string {
  return t(`docs.category.${slug}.title`)
}
</script>

<template>
  <nav
    aria-label="docs tree"
    class="w-full py-4"
  >
    <p class="text-xs font-semibold uppercase tracking-wider text-slate-500 px-3 mb-2">
      {{ t('docs.article.tree_label') }}
    </p>

    <ul role="list" class="space-y-0.5">
      <li
        v-for="categorySlug in DocCategoryValues"
        :key="categorySlug"
      >
        <!-- Category row -->
        <RouterLink
          :to="`${base}/${categorySlug}`"
          class="flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors"
          :class="categorySlug === currentCategory
            ? 'bg-slate-700 text-white font-medium'
            : 'text-slate-400 hover:text-white hover:bg-slate-800'"
          @click="emit('navigate')"
        >
          <span>{{ categoryTitle(categorySlug) }}</span>
          <span
            v-if="(countByCategory[categorySlug] ?? 0) === 0"
            class="text-xs text-slate-600"
          >
            {{ t('docs.hub.coming_soon') }}
          </span>
        </RouterLink>

        <!-- Article rows — only shown for the active category -->
        <ul
          v-if="categorySlug === currentCategory && articlesInCurrentCategory.length > 0"
          role="list"
          class="mt-0.5 ml-3 border-l border-slate-700 pl-3 space-y-0.5"
        >
          <li
            v-for="article in articlesInCurrentCategory"
            :key="article.slug"
          >
            <RouterLink
              :to="`${base}/${categorySlug}/${article.slug}`"
              class="block px-2 py-1.5 rounded text-xs transition-colors"
              :class="article.slug === currentSlug
                ? 'text-emerald-400 font-semibold bg-emerald-500/10'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'"
              :aria-current="article.slug === currentSlug ? 'page' : undefined"
              @click="emit('navigate')"
            >
              {{ article.title }}
            </RouterLink>
          </li>
        </ul>
      </li>
    </ul>
  </nav>
</template>
